import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Share2 } from "lucide-react";

// Brand-accurate X (Twitter) and LinkedIn glyphs. Lucide's Twitter is old — X uses this shape.
const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

const LinkedInIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.852-3.037-1.853 0-2.136 1.446-2.136 2.941v5.665H9.351V9h3.414v1.561h.048c.476-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.777 13.019H3.555V9h3.559v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export default function ShareBar({ chapter }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";
  const shareText = chapter
    ? `"${chapter.title}" — from AMC's ABC's of AI, a plain-English guide to building apps with AI.`
    : "AMC's ABC's of AI — a plain-English guide to building apps with AI.";

  const openShare = (href) => {
    try {
      window.open(href, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.warn("share: window.open failed", e);
    }
  };

  const onX = () =>
    openShare(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`
    );

  const onLinkedIn = () =>
    openShare(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.warn("share: clipboard write failed", e);
      // Fallback for older browsers
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch (err) {
        console.warn("share: fallback copy failed", err);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -18 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.5, duration: 0.4 }}
      className="fixed left-3 sm:left-5 bottom-24 z-[65] flex items-center gap-1.5 sm:gap-2 bg-slate-900/95 backdrop-blur border border-cyan-400/30 rounded-full pl-3 sm:pl-4 pr-2 py-2"
      style={{ boxShadow: "0 0 22px rgba(0,240,255,0.28)" }}
      data-testid="share-bar"
    >
      <div className="hidden md:flex items-center gap-2 pr-2 mr-1 border-r border-cyan-400/20">
        <Share2 size={14} className="text-cyan-300" />
        <span className="text-[11px] uppercase tracking-widest text-slate-300 font-heading whitespace-nowrap">
          Share this chapter with a friend
        </span>
      </div>
      <div className="flex md:hidden items-center pr-1.5 mr-0.5 border-r border-cyan-400/20">
        <Share2 size={14} className="text-cyan-300" />
      </div>

      <button
        onClick={onX}
        data-testid="share-x-btn"
        aria-label="Share on X"
        title="Share on X"
        className="w-11 h-11 rounded-full flex items-center justify-center text-slate-100 hover:text-white hover:bg-cyan-400/15 border border-transparent hover:border-cyan-400/30 transition-colors"
      >
        <XIcon />
      </button>
      <button
        onClick={onLinkedIn}
        data-testid="share-linkedin-btn"
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
        className="w-11 h-11 rounded-full flex items-center justify-center text-slate-100 hover:text-white hover:bg-cyan-400/15 border border-transparent hover:border-cyan-400/30 transition-colors"
      >
        <LinkedInIcon />
      </button>
      <button
        onClick={onCopy}
        data-testid="share-copy-btn"
        aria-label={copied ? "Link copied" : "Copy link"}
        title={copied ? "Link copied!" : "Copy link"}
        className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${
          copied
            ? "bg-emerald-400/20 border-emerald-400/50 text-emerald-200"
            : "text-slate-100 hover:text-white hover:bg-cyan-400/15 border-transparent hover:border-cyan-400/30"
        }`}
      >
        {copied ? <Check size={18} /> : <Copy size={18} />}
      </button>
    </motion.div>
  );
}
